import {
  CATEGORY_BY_ID,
  PRESET_BY_ID,
  applyPlan,
  buildPlan,
  type Profile,
} from "./core/index.js";
import { detectLang, type Lang } from "./ui/i18n.js";
import { defaultProfile, encode, load, persist, type AppState } from "./ui/state.js";
import { flashShare, mount } from "./ui/view.js";
import "./styles.css";

const root = document.getElementById("app");
if (!root) throw new Error("missing #app");

let state: AppState = load(detectLang());

/**
 * `hours` and `buckets` arrive as sparse patches -- the view sends only the
 * category the user touched -- so they merge rather than replace. Everything
 * else is a plain overwrite.
 */
function applyPatch(profile: Profile, patch: Partial<Profile>): Profile {
  const next: Profile = { ...profile, ...patch };
  if (patch.hours) {
    const hours: Record<string, number> = { ...profile.hours };
    for (const [id, value] of Object.entries(patch.hours)) {
      const cat = CATEGORY_BY_ID.get(id);
      if (cat) hours[id] = Math.min(cat.max, Math.max(0, value));
    }
    Object.assign(next, { hours });
  }
  if (patch.buckets) {
    Object.assign(next, { buckets: { ...profile.buckets, ...patch.buckets } });
  }
  if (patch.during) {
    const during = { ...profile.during };
    for (const [host, overlap] of Object.entries(patch.during)) {
      const cat = CATEGORY_BY_ID.get(host);
      // Choosing "nothing else" drops the entry rather than storing a zero, so
      // a cleared overlap disappears from the shared link too.
      if (!cat?.canHost || !overlap.activity || overlap.hours <= 0) {
        delete during[host];
        continue;
      }
      during[host] = {
        activity: overlap.activity,
        hours: Math.min(cat.max, Math.max(0, overlap.hours)),
      };
    }
    Object.assign(next, { during });
  }
  // Retiring before today is not a plan, it is a typo.
  if (next.retirementAge < next.age) {
    Object.assign(next, { retirementAge: next.age });
  }
  return next;
}

const view = mount(root, {
  onProfile(patch) {
    state = { ...state, profile: applyPatch(state.profile, patch) };
    commit();
  },
  onPreset(id: string) {
    const preset = PRESET_BY_ID.get(id);
    if (!preset) return;
    // A preset replaces the whole day rather than merging into it: half of one
    // shape and half of another describes nobody.
    state = {
      ...state,
      profile: {
        ...state.profile,
        hours: { ...preset.hours },
        buckets: { ...state.profile.buckets, ...(preset.buckets ?? {}) },
        during: {},
        workDaysPerWeek: preset.workDaysPerWeek,
        vacationDays: preset.vacationDays,
      },
    };
    commit();
  },
  onTarget(hoursPerDay: number) {
    state = { ...state, target: hoursPerDay };
    commit();
  },
  onApplyPlan() {
    // Writing the plan into the profile makes it inspectable in the ledger
    // rather than a promise sitting next to the numbers it does not affect.
    const plan = buildPlan(state.profile, state.target);
    state = { ...state, profile: applyPlan(state.profile, plan) };
    commit();
  },
  onLang(lang: Lang) {
    state = { ...state, lang };
    commit();
  },
  onReset() {
    state = { ...state, profile: defaultProfile() };
    commit();
  },
  async onShare() {
    const url = `${location.origin}${location.pathname}#${encode(state)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard needs a secure context and is blocked in sandboxed frames.
      // `prompt` is the fallback, and is itself blocked in some of them, so it
      // gets its own guard rather than taking the handler down with it.
      try {
        prompt(url);
      } catch {
        /* nothing left to try; the address bar still holds the same link */
      }
    }
    flashShare(root, state.lang);
  },
});

function commit(): void {
  persist(state);
  view.update(state);
}

window.addEventListener("hashchange", () => {
  const next = load(state.lang);
  state = next;
  view.update(state);
});

commit();
